import VStack from '@/components/core/VStack/VStack'
import { Button, Container, Dialog, DialogContent, DialogHeader, DialogTitle } from '@blockpot-dev/block-pot-design-system'
import DrawnNumberTicket from '../../common/DrawnNumberTicket/DrawnNumberTicket'
import { PlayIcon, ShareIcon } from 'lucide-react'
import HStack from '@/components/core/HStack/HStack'
import { Table, TableRow, TableHeader, TableHead, TableBody, TableCell } from '@/components/ui/table'
import { DisplayDrawnNumberData } from '@/types/lottery/display-drawn-number-data'
import { LotteryEntry, LotteryRoundId } from '@/types/lottery'
import RoundInfoStat from '../../common/RoundInfoStat/RoundInfoStat'
import HighlightDivider from '../../common/HighlightDivider/HighlightDivider'
import useLotteryRound from '@/hooks/contracts/lottery/useLotteryRound'
import { formatNumberMaxDecimalsGreedy } from '@/utilities/formatters'
import { useAccount } from 'wagmi'
import { ZERO_ADDRESS } from '@/web3/constants'
import { createDisplayDrawnNumberData } from '@/utilities/lottery/display-drawn-number-data'
import useFiatConverter from '@/hooks/utilities/useFiatConverter'
import useNativeCurrency from '@/hooks/web3/useNativeCurrency'
import usePlayerEntries from '@/hooks/contracts/lottery/usePlayerEntries'
import { formatDateWithTime } from '@/utilities/time/format-date'

function LotteryEntryRow(props: { entry: LotteryEntry }) {
    const { entry } = props
    const entryEnd = entry.entryStart + entry.amount - 1
    return <TableRow>
        <TableCell className="pl-4">#{entry.index.toString()}</TableCell>
        <TableCell>{entry.amount}</TableCell>
        <TableCell>{entry.entryStart}</TableCell>
        <TableCell>{entryEnd === entry.entryStart ? '-' : entryEnd}</TableCell>
    </TableRow>
}

export type _DrawSummaryDialogProps = {
    open: boolean
    onClose: () => void
    
    formattedChance: string
    formattedDate: string
    roundId: LotteryRoundId
    displayDrawnNumberData: DisplayDrawnNumberData[]
    purchases: LotteryEntry[]
    onReplayDraw: (roundIndex: number) => void
}

export function _DrawSummaryDialog(props: _DrawSummaryDialogProps) {
    const { displayDrawnNumberData, roundId, formattedChance, purchases, formattedDate, onReplayDraw } = props

    const totalTickets = purchases.reduce((acc, purchase) => acc + (purchase.amount), 0)

    // TODO: Add 'You Won' badge for matches
    const { open, onClose } = props
    return <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className='lg:min-w-[576px]'>
            <DialogHeader className='flex flex-row justify-between pr-4'>
                <DialogTitle className='uppercase'>Draw Summary</DialogTitle>
                <span className='text-sm text-secondary-foreground'>{formattedDate}</span>
            </DialogHeader>
            <VStack className='pt-6 gap-6'>
                <Container containerClassName='bg-gray-950' className="p-6">
                    <HStack className='gap-0'>
                        <RoundInfoStat label='Pot No.' value={roundId.potIndex.toFixed(0)} />
                        <HighlightDivider direction='vertical' />
                        <RoundInfoStat label='Round No.' value={`${roundId.roundIndex.toFixed(0)}/${roundId.maxRoundsPerPot.toFixed(0)}`} />
                        <HighlightDivider direction='vertical' />
                        <RoundInfoStat label='Chance' value={formattedChance} />
                    </HStack>
                </Container>
                <VStack className='gap-6'>
                    <span className='text-sm'>Drawn Numbers:</span>
                    <div className='grid grid-cols-2 gap-6'>
                        {
                            displayDrawnNumberData.map((drawnNumber) => (
                                <DrawnNumberTicket key={drawnNumber.number} drawnNumber={drawnNumber} animate={false} />
                            ))
                        }
                    </div>
                </VStack>
                <HStack className='gap-4'>
                    <Button variant='outline' className='flex-1' onClick={() => onReplayDraw(roundId.roundIndex)}>
                        <PlayIcon size={24} />
                        <span>Replay draw</span>
                    </Button>
                    <Button variant='outline' className='flex-1'>
                        <ShareIcon size={24} />
                        <span>Share</span>
                    </Button>
                </HStack>
                <VStack>
                    <HStack className='justify-between'>
                        <span className='text-sm'>Your Purchases:</span>
                        <span className='text-sm'>
                            <span className='text-secondary-foreground'>{'Total Tickets: '}</span>
                            <span className='font-bold text-foreground'>{totalTickets}</span>
                        </span>
                    </HStack>
                    <Table className='after:content-[""] after:absolute after:inset-0 after:border after:border-gray-700 after:rounded-lg'>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="pl-4">Purchase No.</TableHead>
                                <TableHead>Tickets</TableHead>
                                <TableHead>From</TableHead>
                                <TableHead className="pr-4">To</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {
                                purchases.map((purchase) => (
                                    <LotteryEntryRow key={purchase.index} entry={purchase} />
                                ))
                            }
                        </TableBody>
                    </Table>
                </VStack>
            </VStack>
        </DialogContent>
    </Dialog>
}

export type DrawSummaryDialogProps = {
    open: boolean
    onClose: () => void
    onReplayDraw: (roundIndex: number) => void
    roundIndex: number
}

export default function DrawSummaryDialog(props: DrawSummaryDialogProps) {
    const { open, onClose, roundIndex, onReplayDraw } = props
    const round = useLotteryRound(roundIndex)
    const { address } = useAccount()
    const nativeToken = useNativeCurrency()
    const fiatConverter = useFiatConverter()
    const playerEntries = usePlayerEntries(Number(roundIndex))

    if (!round) return null

    let formattedDate = ''
    if (round) {
        formattedDate = formatDateWithTime(new Date(round.drawTime))
    }

    return <_DrawSummaryDialog
        open={open}
        onClose={onClose}
        roundId={{
            potIndex: round.potIndex,
            roundIndex: round.roundIndexInPot + 1,
            maxRoundsPerPot: round.maxRoundsInPot
        }}
        displayDrawnNumberData={createDisplayDrawnNumberData(
            [...round.draws],
            address ?? ZERO_ADDRESS,
            nativeToken,
            fiatConverter
        )}
        formattedChance={`${formatNumberMaxDecimalsGreedy(round.chance / 100, 0, 2)}%`}
        purchases={playerEntries?.entries ?? []}
        formattedDate={formattedDate}
        onReplayDraw={() => onReplayDraw(Number(roundIndex))}
    />
}